/** ArollAuth.tsx — signup, verify, onboarding and email-change pages. */
export const auth = {
  /* -- signup ------------------------------------------------------- */
  'auth.createYourWorkspace': 'Create your workspace',
  'auth.yourName': 'Your name',
  /** Example person name shown as the input placeholder. */
  'auth.yourNamePlaceholder': 'Alex Cutter',
  'auth.workspaceName': 'Workspace name',
  /** Placeholder for workspace name (signup and onboarding). */
  'auth.workspaceNamePlaceholder': 'Your studio',
  'auth.agreePrefix': "I agree to the ",
  'auth.agreeTerms': "PictureUp Terms",
  'auth.agreeAnd': " and ",
  'auth.agreePrivacy': "Privacy Policy",
  'auth.agreeSuffix': "",
  'auth.errorAcceptTerms': "Tick the box to accept the Terms and Privacy Policy.",
  'auth.errorSecurityCheck': "The security check did not pass. Please try again.",
  'auth.email': 'Email',
  /** Example email address shown as the input placeholder. */
  'auth.emailPlaceholder': 'you@studio.com',
  'auth.password': 'Password',
  /** Hints the 10-character minimum password length. */
  'auth.passwordPlaceholder': '10+ characters',
  /** CTA label while the signup request is in flight. */
  'auth.creating': 'Creating',
  'auth.createWorkspace': 'Create workspace',
  'auth.alreadySetUpSignIn': 'Already set up? Sign in',
  'auth.errorEmailTaken': 'That email already has an account. Sign in instead.',
  /** kilogram.com.au/a-roll is a URL — keep it verbatim. */
  'auth.errorSignupsClosed':
    'Signups are not open just yet. Join the waitlist at kilogram.com.au/a-roll.',
  'auth.errorTooManyAttempts': 'Too many attempts from this address. Try again in an hour.',
  'auth.errorPasswordTooShort': 'Check the form: passwords need at least 10 characters.',
  'auth.errorGeneric': 'Something went wrong. Try again in a moment.',
  /* -- signup: verification-sent card ------------------------------- */
  'auth.checkYourEmail': 'Check your email',
  /** First half of "We sent a verification link to {email}. It expires
   *  in 24 hours." — the email address is rendered highlighted between
   *  the prefix and suffix. */
  'auth.verificationSentPrefix': 'We sent a verification link to ',
  /** Second half; follows the highlighted email address. */
  'auth.verificationSentSuffix': '. It expires in 24 hours.',
  'auth.resendLink': 'Resend the link',
  /** Shown after the resend button was pressed. */
  'auth.resentNote': 'Sent again. Check spam too.',
  'auth.backToSignIn': 'Back to sign in',
  /* -- verify ------------------------------------------------------- */
  'auth.verifyingEmail': 'Verifying your email…',
  /** Heading for both expired verify and email-change links. */
  'auth.linkExpired': 'Link expired',
  'auth.verifyLinkInvalid':
    'That verification link is no longer valid. Sign in to get a fresh one, or create the workspace again.',
  'auth.signIn': 'Sign in',
  'auth.signUp': 'Sign up',
  /* -- onboarding --------------------------------------------------- */
  'auth.setUpYourWorkspace': 'Set up your workspace',
  'auth.logo': 'Logo',
  /** Alt text on the uploaded workspace logo image. */
  'auth.workspaceLogoAlt': 'Workspace logo',
  /** Placeholder tile when no logo has been uploaded yet. */
  'auth.logoNone': 'None',
  /** Logo control label while the upload is in flight. */
  'auth.uploading': 'Uploading…',
  /** Logo control label when a logo already exists. */
  'auth.logoReplace': 'Replace',
  /** Logo control label when no logo exists yet. */
  'auth.logoUpload': 'Upload',
  'auth.logoHint': 'Shown on the pages you share with your clients. You can change it any time.',
  'auth.errorLogoUpload': 'Logo upload failed. PNG or SVG under 2MB works best.',
  'auth.firstProject': 'First project',
  /** Placeholder suggesting an asset name; trailing ellipsis intended. */
  'auth.firstProjectPlaceholder': 'Interview edit for...',
  /** CTA label while onboarding submit is in flight. */
  'auth.finishing': 'Finishing',
  'auth.createProject': 'Create project',
  'auth.finishSetup': 'Finish setup',
  'auth.skipForNow': 'Skip for now',
  'auth.errorFinishSetup': 'Could not finish setup. Try again in a moment.',
  /* -- email change ------------------------------------------------- */
  'auth.confirmingNewEmail': 'Confirming your new email…',
  'auth.emailUpdated': 'Email updated',
  /** {email} is the new login email address. */
  'auth.emailUpdatedBody': 'Your login email is now {email}. Use it next time you sign in.',
  /** "PictureUp" is the product name — keep verbatim. */
  'auth.backToAroll': 'Back to PictureUp',
  'auth.emailChangeLinkInvalid':
    'That email change link is no longer valid. Request a fresh one from Settings, then use the new link within an hour.',
  'auth.openSettings': 'Open settings',
  /* -- password reset (forgot + set new) ------------------------------ */
  /** {email} is the address the person typed. The sentence must NOT
   *  confirm that an account exists. */
  'auth.resetTitle': 'Reset password',
  'auth.resetBody': 'Type the email on your account and we’ll send a link to set a new password.',
  'auth.sendResetLink': 'Send reset link',
  'auth.resetSentBody': 'If an account exists for {email}, a reset link is on its way. It expires in one hour.',
  'auth.newPasswordTitle': 'Set a new password',
  'auth.newPasswordBody': 'At least 10 characters. Resetting signs you out everywhere else.',
  'auth.newPassword': 'New password',
  'auth.confirmPassword': 'Confirm password',
  'auth.setPassword': 'Set password',
  'auth.errorPasswordsDontMatch': 'The two passwords don’t match.',
  'auth.errorResetLinkInvalid': 'This link has expired or already been used. Request a new one.',
} as const;
